import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry, useIsOnline, useUploadFileApi } from "src/hooks";
import { type IFile } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setCurrent } from "src/redux/slices/entrySlice";
import { setFiles } from "src/redux/slices/generatorSlice";
import { Notification } from "src/utils/Notification";

/**
 * Custom hook to manage the state and behavior of the Files Modal.
 *
 * @param defaultFiles - An array of default files to be displayed in the modal.
 * @param setPendingFiles - A function to update the state of pending files (optional).
 * @returns An object containing:
 * - `isNewEntry`: A boolean indicating if the current entry is new.
 * - `handleUpload`: A function to handle file uploads.
 * - `isUploading`: A boolean indicating if a file upload is in progress.
 * - `showRemoved`: A boolean indicating if removed files are being shown.
 * - `toggleRemoved`: A function to toggle the visibility of removed files.
 * - `_files`: An array of files to be displayed in the modal.
 * - `removedNumber`: The number of removed files.
 */
export const useFilesModal = (
  defaultFiles: IFile[],
  setPendingFiles?: Dispatch<SetStateAction<File[]>>,
) => {
  const { idType, lbeidOrPartId } = useParams();
  const [showRemoved, setShowRemoved] = useState(false);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const { upload, isUploading } = useUploadFileApi();
  const dispatch = useDispatch();
  const isNewEntry = useIsNewEntry(false);
  const isOnline = useIsOnline();
  const allOfflinePendingFiles = useSelector(
    state => state.pendingRequestsSlice.pendingFiles,
  );
  const offlinePendingFiles = useMemo(
    () =>
      isNewEntry
        ? []
        : allOfflinePendingFiles
            .filter(p => p.entryId === (lbeidOrPartId ?? id))
            .map(p => p.files)
            .flat(),
    [allOfflinePendingFiles, id, isNewEntry, lbeidOrPartId],
  );

  const toggleRemoved = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setShowRemoved(event.currentTarget.checked);
  }, []);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (isUploading) return;

      if (isNewEntry) {
        if (!setPendingFiles) return;

        setPendingFiles(prev => {
          const updatedFiles = [...prev, ...files];
          dispatch(
            setFiles(
              updatedFiles.map((f, i) => ({
                aid: i,
                filename: f.name,
              })),
            ),
          );
          return updatedFiles;
        });
        return;
      }

      const body: Record<string, string> = {};
      body[String(idType)] = lbeidOrPartId ?? id;

      await upload("upload-file", files, body, async res => {
        if (isOnline) {
          dispatch(setFiles(res.attachments));
          dispatch(setCurrent(res.current));
        }
        Notification(
          "Success",
          `The ${
            files.length > 0 ? "files" : "file"
          } has been added successfully`,
          "green",
        );
      });
    },
    [
      dispatch,
      id,
      idType,
      isNewEntry,
      isOnline,
      isUploading,
      lbeidOrPartId,
      setPendingFiles,
      upload,
    ],
  );

  const files = useMemo(
    () => [...defaultFiles, ...offlinePendingFiles],
    [defaultFiles, offlinePendingFiles],
  );

  const _files = useMemo(
    () =>
      isNewEntry ? files : showRemoved ? files : files.filter(f => !f.deleted),
    [files, isNewEntry, showRemoved],
  );

  const removedNumber = useMemo(
    () => defaultFiles.filter(f => !!f.deleted).length,
    [defaultFiles],
  );

  return {
    isNewEntry,
    handleUpload,
    isUploading,
    showRemoved,
    toggleRemoved,
    _files,
    removedNumber,
  };
};
